/**
 * <mox-qty-select> — cart drawer quantity dropdown.
 *
 * Handles the cart API fetch directly so it can show an inventory error
 * message without relying on CartItemsComponent's ref-based error handler.
 */
class MoxQtySelect extends HTMLElement {
  connectedCallback() {
    this._select = this.querySelector('select');
    if (!this._select) return;
    this._prevValue = this._select.value;
    this._onChange = this._onChange.bind(this);
    this._select.addEventListener('change', this._onChange);
  }

  disconnectedCallback() {
    if (this._select) {
      this._select.removeEventListener('change', this._onChange);
    }
  }

  async _onChange() {
    const quantity  = parseInt(this._select.value, 10);
    const line      = parseInt(this.dataset.line, 10);
    const prevValue = this._prevValue;

    this._setLoading(true);
    this._hideError();

    const sections = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      sections.add(el.dataset.sectionId);
    });

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line,
          quantity,
          sections: Array.from(sections).join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      // ── HTTP error (422, etc.) — error de inventario ──────────────────────
      if (!response.ok || data.errors) {
        const msg =
          data.description ||
          data.message ||
          (typeof data.errors === 'string' ? data.errors : null) ||
          'No es posible agregar más unidades de este producto.';

        this._select.value = prevValue;
        this._prevValue    = prevValue;
        this._showError(msg);
        return;
      }

      // ── Shopify capó la qty silenciosamente (sin error HTTP) ──────────────
      const updatedItem = data.items?.[line - 1];
      const actualQty   = updatedItem?.quantity ?? quantity;
      const wasCapped   = actualQty < quantity;

      if (wasCapped) {
        this._select.value = String(actualQty);
        this._prevValue    = String(actualQty);
      } else {
        this._prevValue = String(quantity);
      }

      // Notifica a CartItemsComponent para que re-renderice la sección.
      this.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { data, sections: data.sections },
        })
      );

      // Si hubo cap silencioso: re-muestra el error después del morph.
      if (wasCapped) {
        const dataLine = this.dataset.line;
        const units    = actualQty === 1 ? 'unidad disponible' : 'unidades disponibles';
        const msg      = `Solo hay ${actualQty} ${units} para este producto.`;
        setTimeout(() => {
          const el = document.querySelector(`mox-qty-select[data-line="${dataLine}"]`);
          if (el) el._showError(msg);
        }, 0);
      }
    } catch (err) {
      console.error('[mox-qty-select] fetch error:', err);
      this._select.value = prevValue;
      this._prevValue    = prevValue;
    } finally {
      this._setLoading(false);
    }
  }

  _setLoading(on) {
    this._select.disabled = on;
    this.classList.toggle('mox-qty-select--loading', on);
  }

  _showError(message) {
    const row       = this.closest('tr');
    const container = row?.querySelector('.cart-items__error');
    const text      = row?.querySelector('.cart-item__error-text');
    if (!container || !text) return;
    text.textContent = message;
    container.classList.remove('hidden');
  }

  _hideError() {
    const row       = this.closest('tr');
    const container = row?.querySelector('.cart-items__error');
    if (container) container.classList.add('hidden');
  }
}

customElements.define('mox-qty-select', MoxQtySelect);
